import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import {
  LoadingView,
  ProductSelector,
  IngredientModal,
  useIngredientModal,
  Footer,
} from '../../components';
import { Button, LinkButton, Heading } from '../../atoms';
import { RedirectToLogin } from '../../router';
import { FOOTER_CONTENT } from '../../testing/constants';
import { AccountHeader } from '../AccountHeader';
import { SubtitlePrice } from '../FreshRecipesSelectionForm/styles';
import { StyledEditRecipesFormContainer } from '../EditRecipesFlowContainer/styles';
import {
  userUpdatesBakedRecipesEvent,
  userExpandsBakedRecipeEvent,
  userViewsBakedRecipesPage,
} from './events.js';
import { SubtitleHeading, ButtonContainer, Subtext } from './styles.js';

export const DryRecipesSelectionForm = ({
  cancelButtonText,
  cancelTarget,
  cancelAction,
  defaultRecipes,
  defaultVariantPrice,
  productVariants = [],
  headline,
  subheadline,
  modals,
  onSubmit,
  recipes,
  submitButtonText,
  unavailableRecipes = [],
  links,
  logoUrl,
  logoSrText,
}) => {
  const [selectedRecipes, setSelectedRecipes] = useState(defaultRecipes);
  const reporter = useReporter();

  const availableRecipes = recipes.filter(
    ({ value }) => !unavailableRecipes.includes(value),
  );

  const { modal, onRecipeDetailsClick } = useIngredientModal(modals);
  const onDetailsClickWithReporter = recipe => {
    reporter.tag(userExpandsBakedRecipeEvent(recipe));
    onRecipeDetailsClick(recipe);
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    customer,
    isActive,
    isLoading: isCustomerLoading,
  } = useSessionCustomer();

  const onChange = recipe => {
    setSelectedRecipes(recipe);
  };

  const onSubmitWithReporter = async event => {
    event.preventDefault();
    const availableRecipesValues = availableRecipes.map(({ value }) => value);
    reporter.tag(
      userUpdatesBakedRecipesEvent(
        availableRecipesValues,
        defaultRecipes,
        selectedRecipes,
      ),
    );
    setIsSubmitting(true);
    await onSubmit(selectedRecipes);
    setIsSubmitting(false);
  };

  const newVariantPrice = defaultVariantPrice; // currently don't want to compute the new price based off of variants

  const isSaveDisabled = selectedRecipes.length === 0 || isSubmitting;

  let selectedVariantPrice = !newVariantPrice // if the new variant doesn't exist, then default to default variant price
    ? defaultVariantPrice
    : selectedRecipes.length // if there are recipes selected, then take the selected variant price
    ? newVariantPrice
    : defaultVariantPrice; // we should always have a default variant. falls back to first variant of product in parent container.

  const message = useMemo(
    () =>
      selectedRecipes.length === 0
        ? 'Please select recipes above.'
        : defaultVariantPrice === selectedVariantPrice
        ? ''
        : selectedVariantPrice > defaultVariantPrice
        ? `Because you adjusted your recipes, your price increased from $${defaultVariantPrice}/week to $${selectedVariantPrice}/week.`
        : selectedVariantPrice < defaultVariantPrice
        ? `Because you adjusted your recipes, your price decreased from $${defaultVariantPrice}/week to $${selectedVariantPrice}/week.`
        : '',
    [selectedRecipes, defaultVariantPrice, selectedVariantPrice],
  );

  const price = `$${selectedVariantPrice} PER WEEK`;

  useEffect(() => {
    if (!isCustomerLoading)
      reporter.tag(userViewsBakedRecipesPage(availableRecipes.length));
  }, [reporter, isCustomerLoading, availableRecipes.length]);

  if (!isCustomerLoading && !isActive) {
    return <RedirectToLogin />;
  }

  if (isCustomerLoading) {
    return <LoadingView />;
  }

  return (
    <>
      <AccountHeader
        links={links}
        logoUrl={logoUrl}
        logoSrText={logoSrText}
        name={customer.first_name}
      />
      <StyledEditRecipesFormContainer>
        <Heading
          headingLevel="h1"
          typography={{ desktop: 'heading1', mobile: 'heading2' }}
          headingText={headline}
          position="center"
        />
        <form onSubmit={onSubmitWithReporter}>
          <SubtitleHeading>{subheadline}</SubtitleHeading>
          <ProductSelector
            products={recipes}
            selected={selectedRecipes}
            onChange={onChange}
            onDetailsClick={onDetailsClickWithReporter}
            unavailableProducts={unavailableRecipes}
          />
          <ButtonContainer>
            <div>
              <SubtitlePrice>{price}</SubtitlePrice>
              {message && <Subtext>{message}</Subtext>}
            </div>
            <Button disabled={isSaveDisabled} type="submit">
              {submitButtonText}
            </Button>
            {cancelAction ? (
              <Button onClick={cancelAction} variant="secondary">
                {cancelButtonText}
              </Button>
            ) : (
              <LinkButton href={cancelTarget} variant="secondary">
                {cancelButtonText}
              </LinkButton>
            )}
          </ButtonContainer>
        </form>
      </StyledEditRecipesFormContainer>
      <IngredientModal {...modal} />
      <Footer {...FOOTER_CONTENT} />
    </>
  );
};

DryRecipesSelectionForm.propTypes = {
  cancelButtonText: PropTypes.string.isRequired,
  cancelTarget: PropTypes.string.isRequired,
  cancelAction: PropTypes.func,
  defaultRecipes: PropTypes.array.isRequired,
  headline: PropTypes.string.isRequired,
  modals: PropTypes.array.isRequired,
  isMixed: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  recipes: PropTypes.arrayOf(PropTypes.object.isRequired),
  submitButtonText: PropTypes.string.isRequired,
  subheadline: PropTypes.string.isRequired,
  unavailableRecipes: PropTypes.array,
  links: PropTypes.array.isRequired,
  logoUrl: PropTypes.string.isRequired,
  logoSrText: PropTypes.string.isRequired,
  defaultVariantPrice: PropTypes.number.isRequired,
  productVariants: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      discounted_price: PropTypes.string,
      discounted_price_per_week: PropTypes.string,
      bag_count: PropTypes.string,
      price: PropTypes.string,
      price_per_day: PropTypes.string,
      price_per_week: PropTypes.string,
      recipes: PropTypes.shape({
        dry: PropTypes.arrayOf(PropTypes.string),
        fresh: PropTypes.arrayOf(PropTypes.string),
      }),
      sku: PropTypes.string,
    }).isRequired,
  ),
};
